/**
 * AR-408 — WorldVia member support: contact numbers and service hours, kept
 * in one place so the hours can change without touching a component.
 *
 * Service hours are Eastern Time. Open/closed is decided against
 * `America/New_York` through Intl, so daylight saving is the runtime's job
 * rather than a fixed UTC offset. Holidays are out of scope: the status
 * follows the weekly schedule only.
 */

export const SUPPORT_TIME_ZONE = 'America/New_York';
export const SUPPORT_TIME_ZONE_LABEL = 'ET';

/** The member support line. */
export const SUPPORT_PHONE = '(763) 497-0839';
export const SUPPORT_IATA = '24632716';
/**
 * The agency booking number. Shown as one unbroken number, never as a
 * `tel:` link — it is what a member quotes when a supplier asks for the
 * agency phone number, not a line to dial from the menu.
 */
export const SUPPORT_BOOKING_NUMBER = '7634970312';
export const SUPPORT_BOOKING_NUMBER_HINT =
  'Reference this number if asked for our agency phone number.';
export const HELP_CENTER_URL = 'https://worldvia.pro/pro-help';

/** 0 = Sunday … 6 = Saturday, as `Date#getDay` and Intl report it. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface SupportHoursRule {
  /** Row label as shown to the member, e.g. "Mon–Thu". */
  label: string;
  days: readonly Weekday[];
  /** Minutes after midnight, Eastern Time. `null` on both = closed all day. */
  open: number | null;
  close: number | null;
}

const at = (hour: number, minute = 0): number => hour * 60 + minute;

/** The weekly schedule, Eastern Time. Open is inclusive, close is exclusive. */
export const SUPPORT_HOURS: readonly SupportHoursRule[] = [
  { label: 'Mon–Thu', days: [1, 2, 3, 4], open: at(9), close: at(19) },
  { label: 'Fri', days: [5], open: at(9), close: at(18) },
  { label: 'Sat', days: [6], open: at(9, 30), close: at(15, 30) },
  { label: 'Sun', days: [0], open: null, close: null },
];

/** "9:00 AM", "3:30 PM". */
export function formatSupportTime(minutes: number): string {
  const hour24 = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/** "9:00 AM – 7:00 PM", or "Closed". */
export function formatSupportHoursRule(rule: SupportHoursRule): string {
  if (rule.open === null || rule.close === null) return 'Closed';
  return `${formatSupportTime(rule.open)} – ${formatSupportTime(rule.close)}`;
}

export interface SupportLocalTime {
  weekday: Weekday;
  /** Minutes after midnight in the support time zone. */
  minutes: number;
}

const WEEKDAY_BY_NAME: Record<string, Weekday> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const supportClock = new Intl.DateTimeFormat('en-US', {
  timeZone: SUPPORT_TIME_ZONE,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** The instant `now`, read on the support desk's wall clock (Eastern Time). */
export function supportLocalTime(now: Date = new Date()): SupportLocalTime {
  const parts = supportClock.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const weekday = WEEKDAY_BY_NAME[part('weekday')] ?? 0;
  // Some ICU builds still print midnight as "24" even with hourCycle h23.
  const hour = Number(part('hour')) % 24;
  const minute = Number(part('minute'));
  return { weekday, minutes: hour * 60 + minute };
}

export function supportHoursFor(weekday: Weekday): SupportHoursRule | undefined {
  return SUPPORT_HOURS.find((rule) => rule.days.includes(weekday));
}

/**
 * Whether support is open at `now`, wherever the member is. The comparison
 * happens in Eastern Time, so a member in Los Angeles, London or Sydney gets
 * the same answer as one in New York.
 */
export function isSupportOpen(now: Date = new Date()): boolean {
  const { weekday, minutes } = supportLocalTime(now);
  const rule = supportHoursFor(weekday);
  if (!rule || rule.open === null || rule.close === null) return false;
  return minutes >= rule.open && minutes < rule.close;
}
